import { useEffect, useState } from "react";
import { api, formatApiError } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import StatutBadge from "./StatutBadge";
import { toast } from "sonner";

const MAX = 10;

export default function SessionDialog({ open, onOpenChange, session, preset, onSaved }) {
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [selected, setSelected] = useState([]);
  const [type, setType] = useState("mac_sst");
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/employees").then((r) => setEmployees(r.data.employees)).catch(() => {});
    if (session) {
      setDate(session.date);
      setNotes(session.notes || "");
      setSelected(session.participants.map((p) => p.id));
      setType(session.type || "mac_sst");
    } else if (preset) {
      setDate(preset.date);
      setNotes("");
      setSelected(preset.participant_ids);
      setType(preset.type || "mac_sst");
    } else {
      setDate("");
      setNotes("");
      setSelected([]);
      setType("mac_sst");
    }
  }, [open, session, preset]);

  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX) {
        toast.error(`Maximum ${MAX} participants par session (référentiel INRS)`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const submit = async () => {
    if (!date) {
      toast.error("Choisissez une date de session");
      return;
    }
    setSaving(true);
    try {
      const payload = { date, participant_ids: selected, notes, type };
      if (session) {
        await api.put(`/sessions/${session.id}`, payload);
        toast.success("Session mise à jour");
      } else {
        await api.post("/sessions", payload);
        toast.success("Session planifiée");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="session-dialog">
        <DialogHeader>
          <DialogTitle>{session ? "Modifier la session" : "Planifier une session"} {type === "incendie" ? "de formation incendie" : "MAC SST"}</DialogTitle>
          <DialogDescription>{type === "incendie"
            ? "Recyclage formation incendie : la validité des participants sera renouvelée (durée réglable dans Rappels)."
            : "Référentiel INRS : session de 7h, de 4 à 10 participants, validité du certificat renouvelée 24 mois."}</DialogDescription>
        </DialogHeader>
        {!session && (
          <div className="flex gap-2" data-testid="session-type-selector">
            <button type="button" onClick={() => setType("mac_sst")} data-testid="session-type-mac"
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${type === "mac_sst" ? "border-emerald-700 bg-emerald-950 text-emerald-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
              MAC SST (vert)
            </button>
            <button type="button" onClick={() => setType("incendie")} data-testid="session-type-incendie"
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${type === "incendie" ? "border-sky-700 bg-sky-950 text-sky-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
              Formation incendie (bleu)
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="space-y-2">
            <Label>Date de la session *</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="session-date-input" className="bg-background" />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Lieu, horaires..." data-testid="session-notes-input" className="bg-background" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Participants ({selected.length}/{MAX}) — triés par urgence d'expiration</Label>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border/50" data-testid="session-participants-list">
            {employees.map((e) => (
              <label key={e.id} className="flex items-center gap-3 px-3 py-2 hover:bg-secondary/40 cursor-pointer">
                <Checkbox checked={selected.includes(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`session-participant-${e.id}`} />
                <span className="text-sm flex-1 min-w-0 truncate">{e.nom} {e.prenom} <span className="text-muted-foreground">· {e.service}</span></span>
                <StatutBadge statut={type === "incendie" ? (e.formation_incendie?.statut || "inconnu") : e.statut} joursRestants={type === "incendie" ? e.formation_incendie?.jours_restants : e.jours_restants} />
              </label>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="session-form-submit">
            {saving ? "Enregistrement..." : session ? "Mettre à jour" : "Planifier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
